// src/stores/settings.js
import { defineStore } from 'pinia'
import axios from '@/axios'
import { useAuthStore } from '@/stores/auth'

const defaultSettings = () => ({
  /* PERSONAL / ACCESSIBILITY */
  theme: 1,
  theme_key: 'system',

  high_contrast: false,

  colorblind_mode: 1,
  colorblind_mode_key: 'none',

  font_size: 3,
  font_scale: 1.0,

  reduced_motion: false,
  dyslexia_friendly_font: false,
  tutorial_version: 0,
  tutorial_completed_guides: [],

  /* TABLE / ACCENT COLORING */
  active_tab_branch_id: null,
  active_tab_color: null,
  active_tab_color_hover: null,

  preferred_language_code: 'en',
  preferred_currency_code: 'USD',

  /* NOTIFICATIONS */
  notify_email_enabled: true,
  notify_sms_enabled: false,
  text_notification_method: 'sms',
  telegram_chat_id: null,
  notify_whatsapp_enabled: false,
  notify_in_app_enabled: true,

  notify_email_reunion: true,
  notify_email_heirloom: true,
  notify_email_store: true,
  notify_email_property: true,

  notify_sms_reunion: false,
  notify_sms_heirloom: false,
  notify_sms_store: false,
  notify_sms_property: false,

  notify_whatsapp_reunion: false,
  notify_whatsapp_heirloom: false,
  notify_whatsapp_store: false,
  notify_whatsapp_property: false,

  /* TTS */
  tts_enabled: false,
  tts_autoplay_on_page_load: false,
  tts_scope_code: 1,
  tts_voice: null,
  tts_rate: 1.0,
  tts_pitch: 1.0,
  tts_volume: 1.0
})

const fallbackThemeOptions = [
  { label: 'System', value: 1, key: 'system' },
  { label: 'Dark', value: 2, key: 'dark' },
  { label: 'Light', value: 3, key: 'light' }
]

const fallbackColorblindOptions = [
  { label: 'None', value: 1, key: 'none' },
  { label: 'Deuteranopia', value: 2, key: '2' },
  { label: 'Protanopia', value: 3, key: '3' },
  { label: 'Tritanopia', value: 4, key: '4' }
]

const fallbackFontSizeOptions = [
  { label: 'Small (90%)', value: 1, scale: 0.9 },
  { label: 'Smaller (95%)', value: 2, scale: 0.95 },
  { label: 'Default (100%)', value: 3, scale: 1.0 },
  { label: 'Large (110%)', value: 4, scale: 1.1 },
  { label: 'Larger (120%)', value: 5, scale: 1.2 },
  { label: 'XL (130%)', value: 6, scale: 1.3 },
  { label: 'XXL (140%)', value: 7, scale: 1.4 },
  { label: 'Max (150%)', value: 8, scale: 1.5 }
]

const fallbackTtsScopeOptions = [
  { label: 'None', value: 1 },
  { label: 'Current page only', value: 2 },
  { label: 'All pages (site)', value: 3 }
]

const fallbackLanguageOptions = [
  { language_code: 'en', language_name: 'English', native_name: 'English' }
]

const fallbackCurrencyOptions = [
  { currency_code: 'USD', currency_name: 'US Dollar', symbol: '$', decimal_places: 2 }
]

function themeCodeToKey(value) {
  switch (Number(value)) {
    case 2:
      return 'dark'
    case 3:
      return 'light'
    case 1:
    default:
      return 'system'
  }
}

function themeValueToCode(value) {
  const numericValue = Number(value)

  if ([1, 2, 3].includes(numericValue)) {
    return numericValue
  }

  switch (String(value || '').toLowerCase()) {
    case 'dark':
      return 2
    case 'light':
      return 3
    case 'system':
    default:
      return 1
  }
}

function colorblindCodeToKey(value) {
  switch (Number(value)) {
    case 2:
      return '2'
    case 3:
      return '3'
    case 4:
      return '4'
    case 1:
    default:
      return 'none'
  }
}

function colorblindValueToCode(value) {
  const numericValue = Number(value)

  if ([1, 2, 3, 4].includes(numericValue)) {
    return numericValue
  }

  const normalized = String(value || '').toLowerCase()

  switch (normalized) {
    case 'deuteranopia':
      return 2
    case 'protanopia':
      return 3
    case 'tritanopia':
      return 4
    case 'none':
    default:
      return 1
  }
}

function fontSizeCodeToScale(value) {
  switch (Number(value)) {
    case 1:
      return 0.9
    case 2:
      return 0.95
    case 3:
      return 1.0
    case 4:
      return 1.1
    case 5:
      return 1.2
    case 6:
      return 1.3
    case 7:
      return 1.4
    case 8:
      return 1.5
    default:
      return 1.0
  }
}

function fontScaleToSizeCode(value) {
  const scale = Number(value || 1.0)

  if (scale <= 0.9) return 1
  if (scale <= 0.95) return 2
  if (scale <= 1.0) return 3
  if (scale <= 1.1) return 4
  if (scale <= 1.2) return 5
  if (scale <= 1.3) return 6
  if (scale <= 1.4) return 7

  return 8
}

function normalizeNumber(value, fallback) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return []

  const normalized = [...new Set(
    value
      .map(item => String(item || '').trim())
      .filter(Boolean)
  )]

  const unchanged =
    normalized.length === value.length &&
    normalized.every((item, index) => item === value[index])

  return unchanged ? value : normalized
}

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    settings: defaultSettings(),

    branches: [],

    themeOptions: fallbackThemeOptions,
    colorblindOptions: fallbackColorblindOptions,
    fontSizeOptions: fallbackFontSizeOptions,
    ttsScopeOptions: fallbackTtsScopeOptions,
    languageOptions: fallbackLanguageOptions,
    currencyOptions: fallbackCurrencyOptions,

    loaded: false,
    optionsLoaded: false,
    loading: false,
    error: null
  }),

  getters: {
    activeTabColor(state) {
      return state.settings.active_tab_color || null
    },

    themeKey(state) {
      return state.settings.theme_key || themeCodeToKey(state.settings.theme)
    },

    fontScale(state) {
      return state.settings.font_scale || fontSizeCodeToScale(state.settings.font_size)
    }
  },

  actions: {
    _themeCodeToKey(value) {
      return themeCodeToKey(value)
    },

    _themeValueToCode(value) {
      return themeValueToCode(value)
    },

    _colorblindCodeToKey(value) {
      return colorblindCodeToKey(value)
    },

    _colorblindValueToCode(value) {
      return colorblindValueToCode(value)
    },

    _fontSizeCodeToScale(value) {
      return fontSizeCodeToScale(value)
    },

    _fontScaleToSizeCode(value) {
      return fontScaleToSizeCode(value)
    },

    _normalizeSettings() {
      this.settings.theme = themeValueToCode(this.settings.theme)
      this.settings.theme_key = themeCodeToKey(this.settings.theme)

      this.settings.colorblind_mode = colorblindValueToCode(this.settings.colorblind_mode)
      this.settings.colorblind_mode_key = colorblindCodeToKey(this.settings.colorblind_mode)

      this.settings.font_size = normalizeNumber(this.settings.font_size, 3)
      this.settings.font_scale = fontSizeCodeToScale(this.settings.font_size)

      this.settings.tts_scope_code = normalizeNumber(this.settings.tts_scope_code, 1)
      this.settings.tutorial_version = normalizeNumber(this.settings.tutorial_version, 0)
      this.settings.tutorial_completed_guides = normalizeStringArray(
        this.settings.tutorial_completed_guides
      )
      this.settings.tts_rate = normalizeNumber(this.settings.tts_rate, 1.0)
      this.settings.tts_pitch = normalizeNumber(this.settings.tts_pitch, 1.0)
      this.settings.tts_volume = normalizeNumber(this.settings.tts_volume, 1.0)
    },

    _recalcActiveTabColor() {
      const branchId = this.settings.active_tab_branch_id

      if (!branchId || !Array.isArray(this.branches) || this.branches.length === 0) {
        this.settings.active_tab_color = null
        this.settings.active_tab_color_hover = null
        return
      }

      const branch = this.branches.find(item => item.branch_id === branchId)

      this.settings.active_tab_color =
        branch?.branch_color ||
        branch?.color ||
        null

      this.settings.active_tab_color_hover =
        branch?.branch_color_hover ||
        branch?.color_hover ||
        null
    },

    _settingsPayload(patch = {}) {
      const source = {
        ...this.settings,
        ...(patch || {})
      }

      const fontSize =
        source.font_size ||
        fontScaleToSizeCode(source.font_scale)

      return {
        theme: themeValueToCode(source.theme),
        high_contrast: Boolean(source.high_contrast),
        colorblind_mode: colorblindValueToCode(source.colorblind_mode),
        font_size: normalizeNumber(fontSize, 3),
        reduced_motion: Boolean(source.reduced_motion),
        dyslexia_friendly_font: Boolean(source.dyslexia_friendly_font),
        tutorial_version: normalizeNumber(source.tutorial_version, 0),
        tutorial_completed_guides: normalizeStringArray(source.tutorial_completed_guides),

        active_tab_branch_id: source.active_tab_branch_id || null,
        preferred_language_code: source.preferred_language_code || 'en',
        preferred_currency_code: source.preferred_currency_code || 'USD',

        notify_email_enabled: Boolean(source.notify_email_enabled),
        notify_sms_enabled: Boolean(source.notify_sms_enabled),
        text_notification_method: source.text_notification_method || 'sms',
        telegram_chat_id: source.telegram_chat_id || null,
        notify_whatsapp_enabled: Boolean(source.notify_whatsapp_enabled),
        notify_in_app_enabled: Boolean(source.notify_in_app_enabled),

        notify_email_reunion: Boolean(source.notify_email_reunion),
        notify_email_heirloom: Boolean(source.notify_email_heirloom),
        notify_email_store: Boolean(source.notify_email_store),
        notify_email_property: Boolean(source.notify_email_property),

        notify_sms_reunion: Boolean(source.notify_sms_reunion),
        notify_sms_heirloom: Boolean(source.notify_sms_heirloom),
        notify_sms_store: Boolean(source.notify_sms_store),
        notify_sms_property: Boolean(source.notify_sms_property),

        notify_whatsapp_reunion: Boolean(source.notify_whatsapp_reunion),
        notify_whatsapp_heirloom: Boolean(source.notify_whatsapp_heirloom),
        notify_whatsapp_store: Boolean(source.notify_whatsapp_store),
        notify_whatsapp_property: Boolean(source.notify_whatsapp_property),

        tts_enabled: Boolean(source.tts_enabled),
        tts_autoplay_on_page_load: Boolean(source.tts_autoplay_on_page_load),
        tts_scope_code: normalizeNumber(source.tts_scope_code, 1),
        tts_voice: source.tts_voice || null,
        tts_rate: normalizeNumber(source.tts_rate, 1.0),
        tts_pitch: normalizeNumber(source.tts_pitch, 1.0),
        tts_volume: normalizeNumber(source.tts_volume, 1.0)
      }
    },

    _normalizeThemeOptions(items = []) {
      if (!Array.isArray(items) || items.length === 0) {
        return fallbackThemeOptions
      }

      return items.map(item => ({
        label: item.theme || item.label || themeCodeToKey(item.theme_code || item.value),
        value: normalizeNumber(item.theme_code ?? item.value, 1),
        key: item.theme_key || item.key || themeCodeToKey(item.theme_code ?? item.value)
      }))
    },

    _normalizeColorblindOptions(items = []) {
      if (!Array.isArray(items) || items.length === 0) {
        return fallbackColorblindOptions
      }

      return items.map(item => ({
        label:
          item.colorblind_setting ||
          item.colorblind_mode ||
          item.label ||
          colorblindCodeToKey(item.colorblind_setting_code || item.value),
        value: normalizeNumber(item.colorblind_setting_code ?? item.value, 1),
        key: item.colorblind_mode_key || item.key || colorblindCodeToKey(item.colorblind_setting_code ?? item.value)
      }))
    },

    _normalizeFontSizeOptions(items = []) {
      if (!Array.isArray(items) || items.length === 0) {
        return fallbackFontSizeOptions
      }

      return items.map(item => {
        const value = normalizeNumber(item.font_size_code ?? item.value, 3)
        const scale = normalizeNumber(item.font_scale ?? item.scale, fontSizeCodeToScale(value))

        return {
          label:
            item.font_size_setting ||
            item.font_size ||
            item.label ||
            `${Math.round(scale * 100)}%`,
          value,
          scale
        }
      })
    },

    _normalizeTtsScopeOptions(items = []) {
      if (!Array.isArray(items) || items.length === 0) {
        return fallbackTtsScopeOptions
      }

      return items.map(item => ({
        label: item.scope || item.tts_scope || item.label || 'Scope',
        value: normalizeNumber(item.scope_code ?? item.value, 1)
      }))
    },

    async loadOptions(force = false) {
      if (this.optionsLoaded && !force) return

      try {
        const results = await Promise.allSettled([
          axios.get('/api/dropdown_options/themes'),
          axios.get('/api/dropdown_options/colorblind_settings'),
          axios.get('/api/dropdown_options/font_size_settings'),
          axios.get('/api/dropdown_options/tts_scopes'),
          axios.get('/api/dropdown_options/languages'),
          axios.get('/api/dropdown_options/currencies')
        ])

        const data = (index, fallback) =>
          results[index]?.status === 'fulfilled' ? results[index].value?.data : fallback

        this.themeOptions = this._normalizeThemeOptions(data(0, fallbackThemeOptions))
        this.colorblindOptions = this._normalizeColorblindOptions(data(1, fallbackColorblindOptions))
        this.fontSizeOptions = this._normalizeFontSizeOptions(data(2, fallbackFontSizeOptions))
        this.ttsScopeOptions = this._normalizeTtsScopeOptions(data(3, fallbackTtsScopeOptions))

        const languages = data(4, fallbackLanguageOptions)
        const currencies = data(5, fallbackCurrencyOptions)
        this.languageOptions = Array.isArray(languages) && languages.length ? languages : fallbackLanguageOptions
        this.currencyOptions = Array.isArray(currencies) && currencies.length ? currencies : fallbackCurrencyOptions

        this.optionsLoaded = true
      } catch (e) {
        this.themeOptions = fallbackThemeOptions
        this.colorblindOptions = fallbackColorblindOptions
        this.fontSizeOptions = fallbackFontSizeOptions
        this.ttsScopeOptions = fallbackTtsScopeOptions
        this.languageOptions = fallbackLanguageOptions
        this.currencyOptions = fallbackCurrencyOptions

        this.optionsLoaded = true
      }
    },

    applyToDocument() {
      if (typeof document === 'undefined') return

      this._normalizeSettings()
      this._recalcActiveTabColor()

      const root = document.documentElement
      root.lang = this.settings.preferred_language_code || 'en'

      const themeKey = themeCodeToKey(this.settings.theme)
      const colorblindMode = colorblindCodeToKey(this.settings.colorblind_mode)
      const fontScale = fontSizeCodeToScale(this.settings.font_size)

      const activeColor = this.settings.active_tab_color || null
      const activeColorHover = this.settings.active_tab_color_hover || null

      if (themeKey && themeKey !== 'system') {
        root.dataset.theme = String(themeKey)
      } else {
        root.removeAttribute('data-theme')
      }

      root.dataset.highContrast = String(!!this.settings.high_contrast)
      root.dataset.reducedMotion = String(!!this.settings.reduced_motion)
      root.dataset.dyslexiaFont = String(!!this.settings.dyslexia_friendly_font)

      if (colorblindMode && colorblindMode !== 'none') {
        root.dataset.colorblindMode = String(colorblindMode)
      } else {
        root.removeAttribute('data-colorblind-mode')
      }

      root.style.setProperty('--font-scale', String(fontScale))

      if (activeColor) {
        root.style.setProperty('--user-accent-color', activeColor)
      } else {
        root.style.removeProperty('--user-accent-color')
      }

      if (activeColorHover) {
        root.style.setProperty('--user-accent-color-hover', activeColorHover)
      } else {
        root.style.removeProperty('--user-accent-color-hover')
      }
    },

    applyLocal() {
      this._normalizeSettings()
      this._recalcActiveTabColor()
      this.applyToDocument()
    },

    async load(force = false) {
      if (this.loading) return

      if (this.loaded && !force) {
        await this.loadOptions()
        this.applyToDocument()
        return
      }

      const auth = useAuthStore()
      const userDetailId = auth?.user_detail_id

      if (!userDetailId) {
        await this.loadOptions()
        this.applyToDocument()
        return
      }

      this.loading = true
      this.error = null

      try {
        const [
          settingsRes,
          branchesRes
        ] = await Promise.all([
          axios.get(`/api/family_members/${userDetailId}/settings`),
          axios.get(`/api/family_members/${userDetailId}/branches`),
          this.loadOptions()
        ])

        if (useAuthStore().user_detail_id !== userDetailId) return

        if (settingsRes?.data && typeof settingsRes.data === 'object') {
          this.settings = {
            ...defaultSettings(),
            ...this.settings,
            ...settingsRes.data
          }
        }

        if (Array.isArray(branchesRes?.data)) {
          this.branches = branchesRes.data
        }

        this.applyLocal()
        this.loaded = true
      } catch (e) {
        this.error = e
        await this.loadOptions()
        this.applyToDocument()
      } finally {
        this.loading = false
      }
    },

    async refresh() {
      return this.load(true)
    },

    setLocal(patch) {
      this.settings = {
        ...this.settings,
        ...(patch || {})
      }

      this.applyLocal()
    },

    async save(patch) {
      const auth = useAuthStore()

      if (!auth?.user_detail_id) {
        throw new Error('Missing user_detail_id for settings save.')
      }

      const payload = this._settingsPayload(patch)

      const res = await axios.post(
        `/api/family_members/${auth.user_detail_id}/settings`,
        payload
      )

      if (res?.data && typeof res.data === 'object') {
        this.settings = {
          ...defaultSettings(),
          ...this.settings,
          ...res.data
        }
      } else {
        this.settings = {
          ...this.settings,
          ...payload
        }
      }

      this.applyLocal()

      return res
    },

    resetStore() {
      this.$reset()
      this.clearDocumentSettings()
    },

    clearDocumentSettings() {
      if (typeof document === 'undefined') return

      const root = document.documentElement

      root.removeAttribute('data-theme')
      root.removeAttribute('data-high-contrast')
      root.removeAttribute('data-colorblind-mode')
      root.removeAttribute('data-reduced-motion')
      root.removeAttribute('data-dyslexia-font')

      root.style.removeProperty('--font-scale')
      root.style.removeProperty('--user-accent-color')
      root.style.removeProperty('--user-accent-color-hover')
    }
  },

  persist: {
    key: 'tf_settings',
    paths: [
      'settings',
      'branches',
      'themeOptions',
      'colorblindOptions',
      'fontSizeOptions',
      'ttsScopeOptions',
      'languageOptions',
      'currencyOptions'
    ]
  }
})
